export type RIASECCode = 'R' | 'I' | 'A' | 'S' | 'E' | 'C';

export type Field = 'STEM' | 'Business' | 'Arts & Humanities' | 'Healthcare' | 'Social Sciences' | 'Education' | 'Engineering' | 'Law & Government';

export type InterestTag =
  | 'technology' | 'creative' | 'helping-people' | 'analytical'
  | 'science' | 'business' | 'communication' | 'hands-on'
  | 'leadership' | 'research' | 'design' | 'healthcare'
  | 'education' | 'environment' | 'math' | 'writing'
  | 'social-justice' | 'engineering' | 'data' | 'problem-solving';

export interface Major {
  id: string;
  name: string;
  field: Field;
  description: string;
  careers: string[];
  salaryRange: string;
  growthOutlook: 'High' | 'Medium' | 'Low';
  riasecCodes: RIASECCode[];
  interestTags: InterestTag[];
  skills: string[];
}

export const majors: Major[] = [
  {
    id: 'computer-science',
    name: 'Computer Science',
    field: 'STEM',
    description: 'Study algorithms, programming, and computational systems to build software and solve complex problems.',
    careers: ['Software Engineer', 'Data Scientist', 'Systems Architect', 'DevOps Engineer'],
    salaryRange: '$70k - $150k',
    growthOutlook: 'High',
    riasecCodes: ['I', 'R'],
    interestTags: ['technology', 'analytical', 'problem-solving', 'math'],
    skills: ['Programming', 'Algorithms', 'Critical Thinking', 'Mathematics'],
  },
  {
    id: 'mechanical-engineering',
    name: 'Mechanical Engineering',
    field: 'Engineering',
    description: 'Design, analyze, and manufacture mechanical systems from engines to robotics.',
    careers: ['Mechanical Engineer', 'Product Designer', 'Aerospace Engineer', 'Robotics Engineer'],
    salaryRange: '$65k - $120k',
    growthOutlook: 'Medium',
    riasecCodes: ['R', 'I'],
    interestTags: ['engineering', 'hands-on', 'problem-solving', 'science'],
    skills: ['CAD Design', 'Physics', 'Mathematics', 'Problem Solving'],
  },
  {
    id: 'electrical-engineering',
    name: 'Electrical Engineering',
    field: 'Engineering',
    description: 'Work with electrical systems, circuits, and electronics that power modern technology.',
    careers: ['Electrical Engineer', 'Hardware Engineer', 'Power Systems Engineer', 'Telecommunications Engineer'],
    salaryRange: '$65k - $130k',
    growthOutlook: 'Medium',
    riasecCodes: ['R', 'I'],
    interestTags: ['engineering', 'technology', 'analytical', 'math'],
    skills: ['Circuit Design', 'Mathematics', 'Physics', 'Technical Analysis'],
  },
  {
    id: 'civil-engineering',
    name: 'Civil Engineering',
    field: 'Engineering',
    description: 'Plan and design infrastructure like bridges, roads, and buildings that shape communities.',
    careers: ['Civil Engineer', 'Structural Engineer', 'Urban Planner', 'Construction Manager'],
    salaryRange: '$60k - $110k',
    growthOutlook: 'Medium',
    riasecCodes: ['R', 'I'],
    interestTags: ['engineering', 'hands-on', 'problem-solving', 'environment'],
    skills: ['Structural Analysis', 'Project Management', 'Mathematics', 'CAD'],
  },
  {
    id: 'biology',
    name: 'Biology',
    field: 'STEM',
    description: 'Explore living organisms from molecules to ecosystems, unlocking the mysteries of life.',
    careers: ['Research Scientist', 'Biotechnologist', 'Wildlife Biologist', 'Lab Technician'],
    salaryRange: '$45k - $95k',
    growthOutlook: 'Medium',
    riasecCodes: ['I', 'R'],
    interestTags: ['science', 'research', 'environment', 'analytical'],
    skills: ['Lab Techniques', 'Research Methods', 'Data Analysis', 'Scientific Writing'],
  },
  {
    id: 'chemistry',
    name: 'Chemistry',
    field: 'STEM',
    description: 'Study matter and its transformations to develop new materials, medicines, and technologies.',
    careers: ['Chemist', 'Pharmaceutical Scientist', 'Materials Scientist', 'Quality Control Analyst'],
    salaryRange: '$50k - $100k',
    growthOutlook: 'Medium',
    riasecCodes: ['I', 'R'],
    interestTags: ['science', 'research', 'analytical', 'problem-solving'],
    skills: ['Lab Skills', 'Analytical Thinking', 'Mathematics', 'Research'],
  },
  {
    id: 'mathematics',
    name: 'Mathematics',
    field: 'STEM',
    description: 'Develop mathematical theories and apply quantitative methods to solve real-world problems.',
    careers: ['Actuary', 'Statistician', 'Data Analyst', 'Cryptographer'],
    salaryRange: '$55k - $120k',
    growthOutlook: 'High',
    riasecCodes: ['I', 'C'],
    interestTags: ['math', 'analytical', 'problem-solving', 'data'],
    skills: ['Abstract Reasoning', 'Statistics', 'Proof Writing', 'Computation'],
  },
  {
    id: 'data-science',
    name: 'Data Science',
    field: 'STEM',
    description: 'Extract insights from large datasets using statistics, programming, and machine learning.',
    careers: ['Data Scientist', 'Machine Learning Engineer', 'Business Analyst', 'AI Researcher'],
    salaryRange: '$75k - $160k',
    growthOutlook: 'High',
    riasecCodes: ['I', 'C'],
    interestTags: ['technology', 'data', 'analytical', 'math'],
    skills: ['Statistics', 'Python/R', 'Machine Learning', 'Data Visualization'],
  },
  {
    id: 'nursing',
    name: 'Nursing',
    field: 'Healthcare',
    description: 'Provide direct patient care, health education, and support across clinical settings.',
    careers: ['Registered Nurse', 'Nurse Practitioner', 'Clinical Nurse Specialist', 'Nurse Educator'],
    salaryRange: '$55k - $120k',
    growthOutlook: 'High',
    riasecCodes: ['S', 'I'],
    interestTags: ['healthcare', 'helping-people', 'science', 'hands-on'],
    skills: ['Patient Care', 'Clinical Skills', 'Empathy', 'Critical Thinking'],
  },
  {
    id: 'pre-medicine',
    name: 'Pre-Medicine',
    field: 'Healthcare',
    description: 'Prepare for medical school with a strong foundation in sciences and clinical experience.',
    careers: ['Physician', 'Surgeon', 'Medical Researcher', 'Psychiatrist'],
    salaryRange: '$80k - $300k+',
    growthOutlook: 'High',
    riasecCodes: ['I', 'S'],
    interestTags: ['healthcare', 'science', 'helping-people', 'research'],
    skills: ['Biology', 'Chemistry', 'Empathy', 'Problem Solving'],
  },
  {
    id: 'public-health',
    name: 'Public Health',
    field: 'Healthcare',
    description: 'Improve community health outcomes through policy, education, and disease prevention.',
    careers: ['Epidemiologist', 'Health Educator', 'Policy Analyst', 'Community Health Director'],
    salaryRange: '$45k - $95k',
    growthOutlook: 'High',
    riasecCodes: ['S', 'I'],
    interestTags: ['healthcare', 'helping-people', 'research', 'social-justice'],
    skills: ['Data Analysis', 'Communication', 'Research', 'Program Management'],
  },
  {
    id: 'psychology',
    name: 'Psychology',
    field: 'Social Sciences',
    description: 'Study human behavior and mental processes to understand what drives people.',
    careers: ['Clinical Psychologist', 'Counselor', 'UX Researcher', 'Human Resources Specialist'],
    salaryRange: '$45k - $100k',
    growthOutlook: 'Medium',
    riasecCodes: ['S', 'I'],
    interestTags: ['helping-people', 'research', 'analytical', 'communication'],
    skills: ['Active Listening', 'Research Methods', 'Empathy', 'Data Analysis'],
  },
  {
    id: 'sociology',
    name: 'Sociology',
    field: 'Social Sciences',
    description: 'Examine social structures, institutions, and relationships that shape human society.',
    careers: ['Social Worker', 'Policy Analyst', 'Market Researcher', 'Community Organizer'],
    salaryRange: '$40k - $80k',
    growthOutlook: 'Medium',
    riasecCodes: ['S', 'I'],
    interestTags: ['social-justice', 'research', 'helping-people', 'communication'],
    skills: ['Research Methods', 'Critical Thinking', 'Writing', 'Data Analysis'],
  },
  {
    id: 'political-science',
    name: 'Political Science',
    field: 'Social Sciences',
    description: 'Analyze political systems, public policy, and governance to understand power and democracy.',
    careers: ['Policy Analyst', 'Political Consultant', 'Diplomat', 'Legislative Aide'],
    salaryRange: '$45k - $95k',
    growthOutlook: 'Medium',
    riasecCodes: ['E', 'S'],
    interestTags: ['social-justice', 'leadership', 'communication', 'analytical'],
    skills: ['Research', 'Public Speaking', 'Writing', 'Critical Analysis'],
  },
  {
    id: 'economics',
    name: 'Economics',
    field: 'Business',
    description: 'Study how societies allocate scarce resources, from global markets to individual decisions.',
    careers: ['Economist', 'Financial Analyst', 'Policy Advisor', 'Management Consultant'],
    salaryRange: '$55k - $130k',
    growthOutlook: 'Medium',
    riasecCodes: ['I', 'E'],
    interestTags: ['analytical', 'business', 'math', 'data'],
    skills: ['Quantitative Analysis', 'Statistics', 'Economic Modeling', 'Writing'],
  },
  {
    id: 'business-administration',
    name: 'Business Administration',
    field: 'Business',
    description: 'Learn to manage organizations, lead teams, and drive strategic business decisions.',
    careers: ['Business Manager', 'Operations Director', 'Entrepreneur', 'Management Consultant'],
    salaryRange: '$50k - $120k',
    growthOutlook: 'Medium',
    riasecCodes: ['E', 'C'],
    interestTags: ['business', 'leadership', 'communication', 'problem-solving'],
    skills: ['Leadership', 'Strategic Planning', 'Communication', 'Financial Literacy'],
  },
  {
    id: 'marketing',
    name: 'Marketing',
    field: 'Business',
    description: 'Create strategies to promote products and connect brands with their target audiences.',
    careers: ['Marketing Manager', 'Brand Strategist', 'Digital Marketing Specialist', 'Market Research Analyst'],
    salaryRange: '$45k - $110k',
    growthOutlook: 'Medium',
    riasecCodes: ['E', 'A'],
    interestTags: ['creative', 'business', 'communication', 'data'],
    skills: ['Creative Thinking', 'Data Analysis', 'Communication', 'Digital Tools'],
  },
  {
    id: 'finance',
    name: 'Finance',
    field: 'Business',
    description: 'Manage money, investments, and financial planning for individuals and organizations.',
    careers: ['Financial Analyst', 'Investment Banker', 'Financial Planner', 'Portfolio Manager'],
    salaryRange: '$55k - $150k',
    growthOutlook: 'Medium',
    riasecCodes: ['C', 'E'],
    interestTags: ['business', 'math', 'analytical', 'data'],
    skills: ['Financial Modeling', 'Excel', 'Risk Assessment', 'Quantitative Analysis'],
  },
  {
    id: 'accounting',
    name: 'Accounting',
    field: 'Business',
    description: 'Track, analyze, and report financial information that organizations depend on.',
    careers: ['CPA', 'Auditor', 'Tax Consultant', 'Financial Controller'],
    salaryRange: '$50k - $110k',
    growthOutlook: 'Medium',
    riasecCodes: ['C', 'E'],
    interestTags: ['business', 'math', 'analytical', 'data'],
    skills: ['Attention to Detail', 'Mathematics', 'Tax Law', 'Financial Reporting'],
  },
  {
    id: 'english',
    name: 'English & Literature',
    field: 'Arts & Humanities',
    description: 'Analyze texts, develop strong writing skills, and explore the power of language.',
    careers: ['Writer', 'Editor', 'Content Strategist', 'English Teacher', 'Communications Director'],
    salaryRange: '$40k - $85k',
    growthOutlook: 'Low',
    riasecCodes: ['A', 'S'],
    interestTags: ['writing', 'creative', 'communication', 'education'],
    skills: ['Writing', 'Critical Analysis', 'Research', 'Communication'],
  },
  {
    id: 'graphic-design',
    name: 'Graphic Design',
    field: 'Arts & Humanities',
    description: 'Combine art and technology to communicate ideas through visual content and layouts.',
    careers: ['Graphic Designer', 'UX/UI Designer', 'Art Director', 'Brand Designer'],
    salaryRange: '$40k - $90k',
    growthOutlook: 'Medium',
    riasecCodes: ['A', 'R'],
    interestTags: ['creative', 'design', 'technology', 'communication'],
    skills: ['Adobe Creative Suite', 'Typography', 'Color Theory', 'Visual Communication'],
  },
  {
    id: 'fine-arts',
    name: 'Fine Arts',
    field: 'Arts & Humanities',
    description: 'Develop artistic skills in painting, sculpture, and mixed media to express ideas visually.',
    careers: ['Artist', 'Art Teacher', 'Gallery Curator', 'Illustrator'],
    salaryRange: '$35k - $75k',
    growthOutlook: 'Low',
    riasecCodes: ['A', 'R'],
    interestTags: ['creative', 'design', 'hands-on'],
    skills: ['Drawing', 'Painting', 'Creativity', 'Art History'],
  },
  {
    id: 'music',
    name: 'Music',
    field: 'Arts & Humanities',
    description: 'Study music theory, performance, and composition to pursue a career in the music industry.',
    careers: ['Musician', 'Music Teacher', 'Sound Engineer', 'Music Therapist'],
    salaryRange: '$35k - $80k',
    growthOutlook: 'Low',
    riasecCodes: ['A', 'S'],
    interestTags: ['creative', 'hands-on', 'education'],
    skills: ['Music Theory', 'Performance', 'Ear Training', 'Composition'],
  },
  {
    id: 'communications',
    name: 'Communications',
    field: 'Arts & Humanities',
    description: 'Study media, public relations, and strategic messaging across platforms.',
    careers: ['PR Specialist', 'Journalist', 'Social Media Manager', 'Corporate Communications Director'],
    salaryRange: '$40k - $90k',
    growthOutlook: 'Medium',
    riasecCodes: ['E', 'A'],
    interestTags: ['communication', 'creative', 'writing', 'leadership'],
    skills: ['Writing', 'Public Speaking', 'Media Literacy', 'Strategic Thinking'],
  },
  {
    id: 'education',
    name: 'Education',
    field: 'Education',
    description: 'Prepare to inspire the next generation as a teacher, curriculum designer, or administrator.',
    careers: ['Teacher', 'School Counselor', 'Curriculum Developer', 'Education Administrator'],
    salaryRange: '$40k - $80k',
    growthOutlook: 'Medium',
    riasecCodes: ['S', 'A'],
    interestTags: ['education', 'helping-people', 'communication', 'leadership'],
    skills: ['Communication', 'Patience', 'Lesson Planning', 'Classroom Management'],
  },
  {
    id: 'special-education',
    name: 'Special Education',
    field: 'Education',
    description: 'Work with students who have diverse learning needs to create inclusive educational experiences.',
    careers: ['Special Education Teacher', 'Behavioral Therapist', 'Inclusion Specialist', 'Speech Pathologist'],
    salaryRange: '$42k - $75k',
    growthOutlook: 'High',
    riasecCodes: ['S', 'A'],
    interestTags: ['education', 'helping-people', 'healthcare'],
    skills: ['Patience', 'Adaptability', 'IEP Development', 'Empathy'],
  },
  {
    id: 'environmental-science',
    name: 'Environmental Science',
    field: 'STEM',
    description: 'Study the environment and develop solutions for sustainability and conservation challenges.',
    careers: ['Environmental Scientist', 'Conservation Biologist', 'Sustainability Consultant', 'Park Ranger'],
    salaryRange: '$45k - $90k',
    growthOutlook: 'High',
    riasecCodes: ['I', 'R'],
    interestTags: ['environment', 'science', 'research', 'hands-on'],
    skills: ['Field Research', 'Data Analysis', 'GIS', 'Scientific Writing'],
  },
  {
    id: 'criminal-justice',
    name: 'Criminal Justice',
    field: 'Law & Government',
    description: 'Study the justice system, law enforcement, and corrections to promote public safety.',
    careers: ['Police Officer', 'FBI Agent', 'Probation Officer', 'Legal Analyst'],
    salaryRange: '$40k - $90k',
    growthOutlook: 'Medium',
    riasecCodes: ['S', 'E'],
    interestTags: ['social-justice', 'leadership', 'helping-people', 'analytical'],
    skills: ['Critical Thinking', 'Communication', 'Ethics', 'Research'],
  },
  {
    id: 'pre-law',
    name: 'Pre-Law / Legal Studies',
    field: 'Law & Government',
    description: 'Build a foundation in legal reasoning, ethics, and argumentation for law school.',
    careers: ['Lawyer', 'Judge', 'Legal Consultant', 'Corporate Counsel'],
    salaryRange: '$55k - $180k',
    growthOutlook: 'Medium',
    riasecCodes: ['E', 'I'],
    interestTags: ['social-justice', 'analytical', 'writing', 'communication'],
    skills: ['Legal Writing', 'Argumentation', 'Research', 'Public Speaking'],
  },
  {
    id: 'information-technology',
    name: 'Information Technology',
    field: 'STEM',
    description: 'Manage and secure computer systems, networks, and digital infrastructure.',
    careers: ['IT Manager', 'Network Administrator', 'Cybersecurity Analyst', 'Cloud Architect'],
    salaryRange: '$55k - $130k',
    growthOutlook: 'High',
    riasecCodes: ['R', 'C'],
    interestTags: ['technology', 'problem-solving', 'analytical', 'hands-on'],
    skills: ['Networking', 'Security', 'Troubleshooting', 'System Administration'],
  },
  {
    id: 'biomedical-engineering',
    name: 'Biomedical Engineering',
    field: 'Engineering',
    description: 'Apply engineering principles to healthcare, designing medical devices and technologies.',
    careers: ['Biomedical Engineer', 'Medical Device Designer', 'Clinical Engineer', 'Research Scientist'],
    salaryRange: '$60k - $120k',
    growthOutlook: 'High',
    riasecCodes: ['I', 'R'],
    interestTags: ['engineering', 'healthcare', 'science', 'technology'],
    skills: ['Biology', 'Engineering Design', 'Mathematics', 'Problem Solving'],
  },
  {
    id: 'architecture',
    name: 'Architecture',
    field: 'Engineering',
    description: 'Design buildings and spaces that are functional, sustainable, and aesthetically inspiring.',
    careers: ['Architect', 'Urban Designer', 'Interior Architect', 'Landscape Architect'],
    salaryRange: '$50k - $110k',
    growthOutlook: 'Medium',
    riasecCodes: ['A', 'R'],
    interestTags: ['design', 'creative', 'engineering', 'hands-on'],
    skills: ['CAD/3D Modeling', 'Design Thinking', 'Mathematics', 'Creativity'],
  },
  {
    id: 'international-relations',
    name: 'International Relations',
    field: 'Social Sciences',
    description: 'Study global politics, diplomacy, and cross-cultural dynamics in an interconnected world.',
    careers: ['Diplomat', 'Foreign Service Officer', 'NGO Director', 'Intelligence Analyst'],
    salaryRange: '$45k - $100k',
    growthOutlook: 'Medium',
    riasecCodes: ['E', 'S'],
    interestTags: ['social-justice', 'leadership', 'communication', 'analytical'],
    skills: ['Foreign Languages', 'Research', 'Cultural Competency', 'Writing'],
  },
  {
    id: 'film-media',
    name: 'Film & Media Studies',
    field: 'Arts & Humanities',
    description: 'Create and analyze film, television, and digital media content for diverse audiences.',
    careers: ['Film Director', 'Video Editor', 'Screenwriter', 'Media Producer'],
    salaryRange: '$35k - $95k',
    growthOutlook: 'Medium',
    riasecCodes: ['A', 'E'],
    interestTags: ['creative', 'technology', 'communication', 'design'],
    skills: ['Video Production', 'Storytelling', 'Editing Software', 'Creative Direction'],
  },
  {
    id: 'kinesiology',
    name: 'Kinesiology',
    field: 'Healthcare',
    description: 'Study human movement and exercise science to promote health and athletic performance.',
    careers: ['Physical Therapist', 'Athletic Trainer', 'Exercise Physiologist', 'Sports Coach'],
    salaryRange: '$45k - $90k',
    growthOutlook: 'High',
    riasecCodes: ['S', 'R'],
    interestTags: ['healthcare', 'hands-on', 'science', 'helping-people'],
    skills: ['Anatomy', 'Exercise Science', 'Patient Assessment', 'Communication'],
  },
];

export const fields: Field[] = [
  'STEM', 'Business', 'Arts & Humanities', 'Healthcare',
  'Social Sciences', 'Education', 'Engineering', 'Law & Government',
];

export const interestCategories = [
  { id: 'science', label: 'Science & Research', icon: 'BeakerIcon', tags: ['science', 'research', 'analytical'] as InterestTag[] },
  { id: 'technology', label: 'Technology & Computing', icon: 'ComputerDesktopIcon', tags: ['technology', 'data', 'problem-solving'] as InterestTag[] },
  { id: 'business', label: 'Business & Leadership', icon: 'BriefcaseIcon', tags: ['business', 'leadership', 'communication'] as InterestTag[] },
  { id: 'arts', label: 'Arts & Creativity', icon: 'PaintBrushIcon', tags: ['creative', 'design', 'writing'] as InterestTag[] },
  { id: 'healthcare', label: 'Healthcare & Medicine', icon: 'HeartIcon', tags: ['healthcare', 'helping-people', 'science'] as InterestTag[] },
  { id: 'education', label: 'Education & Teaching', icon: 'AcademicCapIcon', tags: ['education', 'helping-people', 'communication'] as InterestTag[] },
  { id: 'social', label: 'Social Sciences & Justice', icon: 'ScaleIcon', tags: ['social-justice', 'helping-people', 'research'] as InterestTag[] },
  { id: 'engineering', label: 'Engineering & Building', icon: 'WrenchScrewdriverIcon', tags: ['engineering', 'hands-on', 'math'] as InterestTag[] },
];

export const riasecDescriptions: Record<RIASECCode, { name: string; description: string }> = {
  R: { name: 'Realistic', description: 'You enjoy working with tools, machines, and physical materials. You prefer hands-on activities and practical problem solving.' },
  I: { name: 'Investigative', description: 'You love researching, analyzing, and solving complex problems. You\'re curious and enjoy working with data and ideas.' },
  A: { name: 'Artistic', description: 'You value creativity, self-expression, and originality. You thrive in unstructured environments where you can innovate.' },
  S: { name: 'Social', description: 'You enjoy helping, teaching, and counseling others. You\'re empathetic and motivated by making a difference in people\'s lives.' },
  E: { name: 'Enterprising', description: 'You\'re a natural leader who enjoys persuading and managing others. You\'re drawn to business, politics, and entrepreneurship.' },
  C: { name: 'Conventional', description: 'You excel at organizing, managing data, and following through on details. You value structure, accuracy, and efficiency.' },
};
